import { inject, Injectable } from '@angular/core'
import { TranslateLoader } from '@ngx-translate/core'
import { HttpBackend, HttpClient } from '@angular/common/http'
import { Store } from '@ngrx/store'
import { Observable, of, merge } from 'rxjs'
import { catchError, map, switchMap, filter } from 'rxjs/operators'
import { selectLangAsISO } from '@core/store/selectors/user.selectors'
import { I18nApi } from '@core/i18n/api/i18n.api'

@Injectable({ providedIn: 'root' })
export class I18nMergedTranslateLoader implements TranslateLoader {
  private readonly _httpLocal = new HttpClient(inject(HttpBackend))
  private readonly _store = inject(Store)
  private readonly _api = inject(I18nApi)

  public getTranslation(lang: string): Observable<any> {
    const local$ = this._httpLocal.get(`assets/i18n/${lang}.json`).pipe(catchError(() => of({})))

    return local$.pipe(
      switchMap((localData) => {
        const remote$ = this._store.select(selectLangAsISO(lang)).pipe(
          filter((verifiedLang) => !!verifiedLang),
          switchMap((verifiedLang) =>
            this._api.fetchTranslations(verifiedLang).pipe(
              map((res: any) => res?.translations || res),
              catchError(() => of({}))
            )
          ),
          map((remoteData) => this._deepMerge(localData, remoteData))
        )
        return merge(of(localData), remote$)
      })
    )
  }

  private _deepMerge(target: any, source: any): any {
    const output = { ...target }
    if (this._isObject(target) && this._isObject(source)) {
      Object.keys(source).forEach((key) => {
        if (this._isObject(source[key])) {
          if (!(key in target)) {
            output[key] = source[key]
          } else {
            output[key] = this._deepMerge(target[key], source[key])
          }
        } else {
          output[key] = source[key]
        }
      })
    }
    return output
  }

  private _isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item)
  }
}
